/* gg-3d-viewer - interactive product-page 3D model viewer (ES module).
   three.js + GLTFLoader (Draco/Meshopt/KTX2) + OrbitControls + RoomEnvironment.
   FREE orbit always (drag = rotate, wheel = zoom-to-cursor). A Blender-style
   AXIS GIZMO (top-left, rendered in the canvas corner) rotates with the model;
   clicking an axis ball glides the camera (spherical arc, never through the
   model) to that view. A Perspective <-> Isometric toggle + a Home (3/4) button
   live in the toolbar. A light "sphere" (top-right) drags the key light around
   (azimuth + elevation). Reflective floor + soft contact shadow. Alignment
   centres the model on its ROOT/origin (author controls the pose in export).
   Control colours come from the theme/section settings. Prefix g3d-*. Domain B. */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
try { console.log('[g3d] gg-3d-viewer.js v2026-08-22-r49 (module)'); } catch (e) {}

var THREE_VER = '0.160.0';
var ADDON_BASE = 'https://cdn.jsdelivr.net/npm/three@' + THREE_VER + '/examples/jsm/libs/';

function makeCheckerTexture() {
  var size = 1024, squares = 24, cell = size / squares;
  var c = document.createElement('canvas'); c.width = c.height = size;
  var ctx = c.getContext('2d');
  for (var y = 0; y < squares; y++) {
    for (var x = 0; x < squares; x++) {
      ctx.fillStyle = (((x + y) & 1) === 0) ? '#ffffff' : '#111111';
      ctx.fillRect(x * cell, y * cell, cell, cell);
    }
  }
  var tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

// Soft "ambient occlusion" contact shadow: a dark radial blob that sits right
// under the model base, grounding it (a cheap realism cue alongside the cast
// shadow). Stays under the object regardless of where the key light points.
function makeContactShadowTexture() {
  var s = 256;
  var c = document.createElement('canvas'); c.width = c.height = s;
  var x = c.getContext('2d');
  var g = x.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  g.addColorStop(0.0, 'rgba(0,0,0,0.40)');
  g.addColorStop(0.45, 'rgba(0,0,0,0.16)');
  g.addColorStop(1.0, 'rgba(0,0,0,0)');
  x.fillStyle = g; x.fillRect(0, 0, s, s);
  return new THREE.CanvasTexture(c);
}

// Meshy-style backdrop rendered as a FULLSCREEN SHADER pass with ordered
// (Bayer 8x8) dithering. The gradient is computed per-pixel and a structured
// +/-0.5/255 dither is added in DISPLAY space right before quantization - this
// is the standard pro technique: it physically breaks 8-bit banding ("лесенка")
// while being sub-pixel and invisible (NOT film grain / random noise).
var BG_VERT = [
  'varying vec2 vUv;',
  'void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }'
].join('\n');
var BG_FRAG = [
  'varying vec2 vUv;',
  'uniform vec3 uTop; uniform vec3 uBottom;',
  'float bayer2(vec2 a){ a = floor(a); return fract(a.x * 0.5 + a.y * a.y * 0.75); }',
  'float bayer4(vec2 a){ return bayer2(0.5 * a) * 0.25 + bayer2(a); }',
  'float bayer8(vec2 a){ return bayer4(0.5 * a) * 0.25 + bayer2(a); }',
  'void main(){',
  '  vec3 col = mix(uBottom, uTop, vUv.y);',     // gentle vertical gradient (no radial "circle")
  '  gl_FragColor = vec4(col, 1.0);',
  '  #include <colorspace_fragment>',            // linear -> display (sRGB)
  '  gl_FragColor.rgb += (bayer8(gl_FragCoord.xy) - 0.5) / 255.0;',   // ordered dither kills banding
  '}'
].join('\n');

// Ordered Bayer 8x8 dither, reused to break banding in ALPHA gradients (cast
// shadow + AO blob) - the built-in material.dithering only touches RGB, so a
// shadow whose gradient lives in alpha bands unless we dither the alpha itself.
var G3D_BAYER_GLSL = [
  'float g3dBayer2(vec2 a){ a = floor(a); return fract(a.x * 0.5 + a.y * a.y * 0.75); }',
  'float g3dBayer4(vec2 a){ return g3dBayer2(0.5 * a) * 0.25 + g3dBayer2(a); }',
  'float g3dBayer8(vec2 a){ return g3dBayer4(0.5 * a) * 0.25 + g3dBayer2(a); }'
].join('\n');
function ditherMaterialAlpha(material) {
  material.onBeforeCompile = function (shader) {
    shader.fragmentShader = G3D_BAYER_GLSL + '\n' + shader.fragmentShader.replace(
      '#include <premultiplied_alpha_fragment>',
      'gl_FragColor.a += (g3dBayer8(gl_FragCoord.xy) - 0.5) / 255.0;\n\t#include <premultiplied_alpha_fragment>'
    );
  };
  material.needsUpdate = true;
}

// ---- Baked CONTACT SHADOW (drei/sketchfab-style) ----
// Render the model straight down into a target as a "darkness by height" silhouette
// (parts near the floor = dark, higher = faint), blur it, then project that blurred
// texture onto the ground. Soft, model-shaped, and - because it's a blurred texture
// sampled linearly - it has NO shadow-map stair-steps and NO 8-bit banding.
// SKINNING BELONGS HERE, and its absence was a real bug.
//
// This shader is what the contact shadow is baked from, and it used to read
// `position` straight out of the buffer. On a static mesh that IS the mesh. On
// a RIGGED one it is the BIND pose - which, for a folding carton, is the flat
// die-cut lying open, several times wider than the folded box. So the baked
// shadow was the silhouette of the unfolded sheet: a slab of darkness across
// the whole plate, unrelated to the shape standing on it. Delete the rig and
// the very same file baked correctly, because bind pose and final pose were
// then one and the same.
//
// three sets USE_SKINNING from the OBJECT (object.isSkinnedMesh), not from the
// material, and declares skinIndex/skinWeight for any non-raw ShaderMaterial -
// so one shader covers both cases: on a mesh with no rig the chunks compile to
// nothing at all.
var SHADOW_VERT = [
  '#include <skinning_pars_vertex>',
  'uniform float uGroundY; uniform float uHeight;',
  'varying float vH;',
  'void main(){',
  '  #include <skinbase_vertex>',
  '  vec3 transformed = vec3( position );',
  '  #include <skinning_vertex>',
  '  vec4 wp = modelMatrix * vec4( transformed, 1.0 );',
  '  vH = clamp((wp.y - uGroundY) / uHeight, 0.0, 1.0);',
  '  gl_Position = projectionMatrix * viewMatrix * wp;',
  '}'
].join('\n');
var SHADOW_FRAG = [
  'varying float vH;',
  'void main(){',
  '  float dark = pow(1.0 - vH, 3.0);',   // tight to the CONTACT: only the lowest (resting) parts darken
  '  gl_FragColor = vec4(vec3(dark), 1.0);',
  '}'
].join('\n');
// Separable gaussian blur (9-tap) for a fullscreen quad.
var BLUR_FRAG = [
  'varying vec2 vUv;',
  'uniform sampler2D tDiffuse; uniform vec2 uTexel; uniform vec2 uDir;',
  'void main(){',
  '  vec2 o = uDir * uTexel;',
  '  vec4 s = texture2D(tDiffuse, vUv) * 0.18;',
  '  s += texture2D(tDiffuse, vUv + o) * 0.15; s += texture2D(tDiffuse, vUv - o) * 0.15;',
  '  s += texture2D(tDiffuse, vUv + o * 2.0) * 0.12; s += texture2D(tDiffuse, vUv - o * 2.0) * 0.12;',
  '  s += texture2D(tDiffuse, vUv + o * 3.0) * 0.09; s += texture2D(tDiffuse, vUv - o * 3.0) * 0.09;',
  '  s += texture2D(tDiffuse, vUv + o * 4.0) * 0.05; s += texture2D(tDiffuse, vUv - o * 4.0) * 0.05;',
  '  gl_FragColor = s;',
  '}'
].join('\n');
// Ground material: projects the baked shadow texture (same camera that baked it,
// so alignment is exact - no flips) and dithers the alpha.
var SG_VERT = [
  'uniform mat4 uShadowMatrix;',
  'varying vec4 vSC;',
  'void main(){',
  '  vec4 wp = modelMatrix * vec4(position, 1.0);',
  '  vSC = uShadowMatrix * wp;',
  '  gl_Position = projectionMatrix * viewMatrix * wp;',
  '}'
].join('\n');
var SG_FRAG = [
  'uniform sampler2D uShadowMap; uniform float uOpacity; uniform vec2 uOffset;',
  'varying vec4 vSC;',
  G3D_BAYER_GLSL,
  'void main(){',
  '  vec2 uv = (vSC.xy / vSC.w) * 0.5 + 0.5 + uOffset;',   // uOffset leans the soft pool with the light
  '  float a = 0.0;',
  '  if (uv.x > 0.001 && uv.x < 0.999 && uv.y > 0.001 && uv.y < 0.999) a = texture2D(uShadowMap, uv).g;',
  '  a *= uOpacity;',
  '  a += (g3dBayer8(gl_FragCoord.xy) - 0.5) / 255.0;',
  '  gl_FragColor = vec4(0.0, 0.0, 0.0, a);',
  '}'
].join('\n');

// A round, camera-facing "ball" sprite for the gizmo - solid (with a letter) or
// a hollow ring for the negative axis end.
// `hot` swaps ONLY the letter's colour: dark grey at rest, near-white under the
// pointer. The ball keeps its axis colour, its size and its shape, so nothing
// about the gizmo's look changes - it just answers when you are on it, which is
// the one thing it never did.
function gizmoBallTexture(color, label, hot) {
  var s = 72; var c = document.createElement('canvas'); c.width = c.height = s;
  var x = c.getContext('2d');
  x.fillStyle = color;
  x.beginPath(); x.arc(s / 2, s / 2, s / 2 - 3, 0, Math.PI * 2); x.fill();
  if (label) {
    x.fillStyle = hot ? '#ffffff' : 'rgba(38,38,38,0.78)';
    x.font = 'bold 40px system-ui,-apple-system,Segoe UI,Arial';
    x.textAlign = 'center'; x.textBaseline = 'middle';
    x.fillText(label, s / 2, s / 2 + 2);
  }
  var t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}
function gizmoRingTexture(color) {
  var s = 72; var c = document.createElement('canvas'); c.width = c.height = s;
  var x = c.getContext('2d');
  x.fillStyle = 'rgba(0,0,0,0.001)'; x.fillRect(0, 0, s, s);   // clickable area
  x.strokeStyle = color; x.lineWidth = 7;
  x.beginPath(); x.arc(s / 2, s / 2, s / 2 - 6, 0, Math.PI * 2); x.stroke();
  var t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
}

// DO THE CLIPS FIGHT OVER THE SAME NODES?
//
// This is the one question that decides whether several clips may play at once.
// Exporters write multi-clip files for two completely different reasons:
//
//   - one clip per moving PART (a lid, a drawer, a rotor). The clips touch
//     disjoint nodes, they were authored to run together, and playing only one
//     leaves the rest of the machine standing still.
//   - one clip per ALTERNATIVE (walk, run, survey). Every clip drives the same
//     skeleton, they were authored as choices, and running them together is a
//     blend of poses nobody asked for - which is exactly what a fox playing all
//     three at once looks like.
//
// Nothing in glTF labels which kind a file is, but the tracks give it away: a
// track is named "<node>.<property>", so shared node names mean the clips
// overwrite each other. Overlap = alternatives, play one. No overlap = parts,
// play them all.
function clipsOverlap(clips) {
  if (!clips || clips.length < 2) return false;
  var seen = Object.create(null);
  for (var i = 0; i < clips.length; i++) {
    var own = Object.create(null);
    var tracks = clips[i].tracks || [];
    for (var j = 0; j < tracks.length; j++) {
      var n = tracks[j].name || '';
      var dot = n.lastIndexOf('.');   // last, not first: a node name may contain dots
      own[dot > 0 ? n.slice(0, dot) : n] = 1;
    }
    for (var k in own) {
      if (seen[k]) return true;
      seen[k] = 1;
    }
  }
  return false;
}

function initViewer(root) {
  if (root.dataset.g3dInit === '1') return;
  root.dataset.g3dInit = '1';

  var url = root.getAttribute('data-model-url');
  // A cast shadow says "this object is standing on something". On a product
  // shot meant to float free of any surface it says the wrong thing, so it is
  // a switch. Read before the renderer so the shadow pass is never even set up.
  var shadowsOn = root.getAttribute('data-shadow') !== '0';
  var stage = root.querySelector('[data-g3d-stage]');
  var loadingEl = root.querySelector('[data-g3d-loading]');
  var loadingTxt = loadingEl ? loadingEl.querySelector('span:not(.g3d-spinner)') : null;
  var errorEl = root.querySelector('[data-g3d-error]');
  if (!url || !stage) return;

  function fail(msg) {
    if (loadingEl) loadingEl.style.display = 'none';
    if (errorEl) { errorEl.hidden = false; if (msg) errorEl.textContent = msg; }
    try { console.error('[g3d]', msg); } catch (e) {}
  }

  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var renderer;
  try { renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); }
  catch (e) { fail('WebGL is not available in this browser.'); return; }
  // Supersample (SSAA): render ~1.6x the display resolution and let the browser
  // downscale the canvas. Smooths contour aliasing ("рябь" on edges) far better
  // than a post pass, and the downsample averages the dithered gradient so
  // banding disappears too. Capped so big/retina screens don't tank perf.
  var SS = 1.8;   // supersample factor - raise toward 2.2 for more crispness (costs GPU)
  renderer.setPixelRatio(Math.min((window.devicePixelRatio || 1) * SS, 3));
  // TONE MAPPING, and why white was not white.
  //
  // ACES Filmic is a film-emulation curve: it was built to make images look
  // like cinema, and part of that look is warming the highlights. On a product
  // it reads as a yellow cast on anything pale - the exact "белый не белый"
  // complaint - and no amount of neutral lighting fixes it, because the shift
  // happens after the lighting, on the way to the screen.
  //
  // Khronos PBR Neutral (THREE.NeutralToneMapping) exists for this: it was
  // designed for commerce, and its whole point is that it compresses highlights
  // WITHOUT moving hue, so a white product stays white and a colour stays the
  // colour the author authored. It is why model-viewer moved off ACES too.
  //
  // Falls back to ACES if the pinned three is older than r162, so an accidental
  // downgrade of the import map degrades the look instead of breaking the page.
  var TONE_NEUTRAL = THREE.NeutralToneMapping || THREE.ACESFilmicToneMapping;
  var toneWanted = (root.getAttribute('data-tone') === 'filmic')
    ? THREE.ACESFilmicToneMapping
    : TONE_NEUTRAL;
  renderer.toneMapping = toneWanted;
  // Neutral rolls off later than ACES, so it needs slightly less push to sit at
  // the same brightness.
  renderer.toneMappingExposure = (toneWanted === THREE.ACESFilmicToneMapping) ? 1.04 : 1.0;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  // Hard-ish directional shadow (PCF, no soft radius) = a crisp side shadow with
  // NO wide penumbra gradient, so it can't band. The soft grounding comes from
  // the separately-baked contact shadow.
  renderer.shadowMap.enabled = shadowsOn;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  renderer.shadowMap.autoUpdate = false;
  renderer.autoClear = false;   // we clear manually so the gizmo can overlay
  stage.appendChild(renderer.domElement);

  var scene = new THREE.Scene();
  var pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

  // Dithered gradient backdrop, drawn as a fullscreen shader pass (see BG_FRAG).
  // Neutral greys. Only a fallback: the real values come from the section's
  // colour settings through --g3d-bg-1/2 just below.
  var bg1 = '#F2F2F2', bg2 = '#DEDEDE';
  try { var _b1 = getComputedStyle(root).getPropertyValue('--g3d-bg-1').trim(); if (_b1) bg1 = _b1; } catch (e) {}
  try { var _b2 = getComputedStyle(root).getPropertyValue('--g3d-bg-2').trim(); if (_b2) bg2 = _b2; } catch (e) {}
  var bgScene = new THREE.Scene();
  var bgCam = new THREE.Camera();
  var bgUniforms = {
    uTop: { value: new THREE.Color(bg1) },        // lighter at the top
    uBottom: { value: new THREE.Color(bg2) }      // slightly deeper at the bottom
  };
  var bgMat = new THREE.ShaderMaterial({ uniforms: bgUniforms, depthTest: false, depthWrite: false, vertexShader: BG_VERT, fragmentShader: BG_FRAG });
  bgScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), bgMat));

  // WHITE BALANCE.
  //
  // The rig, the environment and the tone curve are all neutral, so anything
  // still reading warm is coming out of the MODEL - a warm base colour, or a
  // metal whose tint colours everything it reflects. That is the author's
  // material and the viewer must not silently rewrite it.
  //
  // What a photographer does instead is balance the light. 0 leaves everything
  // exactly as authored; negative cools the lamps, which cancels a warm
  // material; positive warms them. Applied to the lamps only - the environment
  // stays neutral, so reflections keep their shape.
  var tempPct = parseFloat(root.getAttribute('data-temp') || '0');
  if (!isFinite(tempPct)) tempPct = 0;
  var lightTint = new THREE.Color(0xffffff);
  if (tempPct !== 0) {
    var t = Math.max(-100, Math.min(100, tempPct)) / 100;
    // Gentle on purpose: at the extremes this is a nudge, not a colour gel.
    // Blue and red move in opposite directions, green is left alone, so the
    // result stays on the warm-cool axis instead of drifting into a hue.
    lightTint = new THREE.Color(1 + 0.16 * t, 1, 1 - 0.18 * t);
  }

  var key = new THREE.DirectionalLight(0xffffff, 2.1);
  key.color.copy(lightTint);
  key.castShadow = shadowsOn;            // crisp directional "side" shadow
  key.shadow.mapSize.set(4096, 4096);    // crisp edge (not chunky)
  key.shadow.bias = -0.0003;             // depth bias (flat floor) carries acne protection instead of normalBias
  var fill = new THREE.DirectionalLight(0xffffff, 0.6);
  var rim = new THREE.DirectionalLight(0xffffff, 1.3);
  fill.color.copy(lightTint); rim.color.copy(lightTint);
  scene.add(key); scene.add(fill); scene.add(rim);
  // NEUTRAL BOUNCE. This used to be a warm beige (0xe8dcc8), the only tinted
  // light in an otherwise white rig, and it is what put a yellow cast on
  // everything the eye reads as "the render". Grey, not white: the bounce off a
  // floor is darker than the sky above it, and that difference is what makes a
  // model sit in a room rather than float in a void. Darkness carries it; hue
  // does not have to.
  var hemi = new THREE.HemisphereLight(0xffffff, 0xb4b4b4, 0.25);
  hemi.color.copy(lightTint);
  scene.add(hemi);
  var lightTarget = new THREE.Object3D();
  scene.add(lightTarget);
  key.target = lightTarget; fill.target = lightTarget; rim.target = lightTarget;

  // ---- Baked contact shadow (see SHADOW_* / BLUR_FRAG / SG_* shaders) ----
  var SHADOW_RES = 1024;
  var shadowRT = new THREE.WebGLRenderTarget(SHADOW_RES, SHADOW_RES, { depthBuffer: true, stencilBuffer: false });
  var shadowRT2 = new THREE.WebGLRenderTarget(SHADOW_RES, SHADOW_RES, { depthBuffer: false, stencilBuffer: false });
  shadowRT.texture.minFilter = shadowRT.texture.magFilter = THREE.LinearFilter;
  shadowRT2.texture.minFilter = shadowRT2.texture.magFilter = THREE.LinearFilter;
  var shadowCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  var shadowMatrix = new THREE.Matrix4();
  var shadowDepthMat = new THREE.ShaderMaterial({
    uniforms: { uGroundY: { value: 0 }, uHeight: { value: 1 } },
    vertexShader: SHADOW_VERT, fragmentShader: SHADOW_FRAG,
    side: THREE.DoubleSide, depthTest: false, depthWrite: false,
    blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation,
    blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor
  });
  var blurScene = new THREE.Scene();
  var blurCam = new THREE.Camera();
  var blurMat = new THREE.ShaderMaterial({
    uniforms: { tDiffuse: { value: null }, uTexel: { value: new THREE.Vector2() }, uDir: { value: new THREE.Vector2() } },
    vertexShader: BG_VERT, fragmentShader: BLUR_FRAG, depthTest: false, depthWrite: false
  });
  blurScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), blurMat));
  var sgMat = new THREE.ShaderMaterial({
    uniforms: { uShadowMap: { value: shadowRT.texture }, uShadowMatrix: { value: new THREE.Matrix4() }, uOpacity: { value: 0.6 }, uOffset: { value: new THREE.Vector2() } },
    vertexShader: SG_VERT, fragmentShader: SG_FRAG, transparent: true, depthWrite: false
  });
  var shadowGround = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), sgMat);
  shadowGround.rotation.x = -Math.PI / 2;
  shadowGround.renderOrder = 2;   // draw the contact patch over the directional shadow
  shadowGround.visible = false;
  scene.add(shadowGround);

  // Plane that catches the crisp DIRECTIONAL cast shadow (follows the key light).
  // Its density GRADES from dark near the model base to a light fade at the tip,
  // and is naturally CLIPPED to the crisp shadow shape (ShadowMaterial alpha is 0
  // outside the shadow). Alpha is dithered so the gradient can't band.
  var castGroundMat = new THREE.ShadowMaterial({ opacity: 0.45 });
  castGroundMat.onBeforeCompile = function (shader) {
    shader.uniforms.uFade = { value: new THREE.Vector2(R * 0.6, R * 2.6) };
    shader.vertexShader = 'varying vec3 vWorldCS;\n' + shader.vertexShader.replace(
      '#include <worldpos_vertex>',
      '#include <worldpos_vertex>\n\tvWorldCS = worldPosition.xyz;'
    );
    shader.fragmentShader = (G3D_BAYER_GLSL + '\nvarying vec3 vWorldCS;\nuniform vec2 uFade;\n' + shader.fragmentShader)
      .replace(
        'gl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) );',
        'gl_FragColor = vec4( color, opacity * ( 1.0 - getShadowMask() ) );\n\tgl_FragColor.a *= 1.0 - 0.8 * smoothstep(uFade.x, uFade.y, length(vWorldCS.xz));'
      )
      .replace(
        '#include <premultiplied_alpha_fragment>',
        'gl_FragColor.a += (g3dBayer8(gl_FragCoord.xy) - 0.5) / 255.0;\n\t#include <premultiplied_alpha_fragment>'
      );
  };
  castGroundMat.needsUpdate = true;
  var castGround = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), castGroundMat);
  castGround.rotation.x = -Math.PI / 2;
  castGround.receiveShadow = true;
  castGround.visible = false;
  scene.add(castGround);

  // AO blob - only used as a fallback if the baked shadow ever fails.
  var contactMat = new THREE.MeshBasicMaterial({ map: makeContactShadowTexture(), transparent: true, depthWrite: false, opacity: 0.9 });
  ditherMaterialAlpha(contactMat);
  var contact = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), contactMat);
  contact.rotation.x = -Math.PI / 2;
  contact.renderOrder = 1;
  contact.visible = false;
  scene.add(contact);

  function bakeShadow() {
    var box = new THREE.Box3().setFromObject(spinGroup, true);
    var hx = Math.max(box.max.x - box.min.x, 1e-3) * 0.5 * 1.35;   // tight padding - contact hugs the footprint
    var hz = Math.max(box.max.z - box.min.z, 1e-3) * 0.5 * 1.35;
    // Measured against the FLOOR, not against whatever the model's own lowest
    // point happens to be in this frame. A raised pose then darkens less, which
    // is what a contact shadow is supposed to say. On a static model groundY is
    // the base, so this is the same arithmetic it always was.
    var lowY = Math.min(box.min.y, groundY);
    var span = Math.max(box.max.y - lowY, 1e-3);
    shadowCam.left = -hx; shadowCam.right = hx; shadowCam.top = hz; shadowCam.bottom = -hz;
    shadowCam.near = 0; shadowCam.far = span * 1.1;
    shadowCam.position.set(0, box.max.y + span * 0.04, 0);
    shadowCam.up.set(0, 0, -1);                 // avoid the straight-down gimbal
    shadowCam.lookAt(0, lowY, 0);
    shadowCam.updateMatrixWorld(true);
    shadowCam.updateProjectionMatrix();
    shadowDepthMat.uniforms.uGroundY.value = groundY;
    shadowDepthMat.uniforms.uHeight.value = Math.max(box.max.y - groundY, 1e-3);

    var prevTarget = renderer.getRenderTarget();
    var prevShadow = renderer.shadowMap.enabled;
    renderer.shadowMap.enabled = false;   // don't run the shadow-map pass during the bake
    shadowGround.visible = false; contact.visible = false; castGround.visible = false;
    scene.overrideMaterial = shadowDepthMat;
    renderer.setRenderTarget(shadowRT);
    renderer.setClearColor(0x000000, 1); renderer.clear(true, true, false);
    renderer.render(scene, shadowCam);
    scene.overrideMaterial = null;
    renderer.shadowMap.enabled = prevShadow;
    renderer.shadowMap.needsUpdate = true;   // re-render the directional shadow map next frame
    castGround.visible = true;

    blurMat.uniforms.uTexel.value.set(1 / SHADOW_RES, 1 / SHADOW_RES);
    for (var i = 0; i < 2; i++) {   // light blur = a tight, soft contact patch (not a big detached blob)
      blurMat.uniforms.tDiffuse.value = shadowRT.texture; blurMat.uniforms.uDir.value.set(1.2, 0);
      renderer.setRenderTarget(shadowRT2); renderer.clear(true, false, false); renderer.render(blurScene, blurCam);
      blurMat.uniforms.tDiffuse.value = shadowRT2.texture; blurMat.uniforms.uDir.value.set(0, 1.2);
      renderer.setRenderTarget(shadowRT); renderer.clear(true, false, false); renderer.render(blurScene, blurCam);
    }
    renderer.setRenderTarget(prevTarget);
    renderer.setClearColor(0x000000, 0);

    shadowMatrix.multiplyMatrices(shadowCam.projectionMatrix, shadowCam.matrixWorldInverse);
    sgMat.uniforms.uShadowMatrix.value.copy(shadowMatrix);
    sgMat.uniforms.uShadowMap.value = shadowRT.texture;
    shadowGround.scale.set(R * 16, R * 16, 1);
    shadowGround.position.set(0, groundY - R * 0.0006, 0);   // on the floor, just above castGround
    shadowGround.visible = true;
  }

  // Dual cameras (perspective + orthographic for isometric), each with its own
  // OrbitControls. Only the active controls are enabled, so free orbit always
  // works on whichever projection is active.
  var persp = new THREE.PerspectiveCamera(45, 1, 0.01, 2000);
  var ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 5000);
  // Initial projection from the theme setting (defaults to isometric).
  var projection = (root.getAttribute('data-projection') === 'perspective') ? 'perspective' : 'isometric';
  var START_PROJ = projection;   // reset returns here (same projection the page opened with)
  var camera = projection === 'isometric' ? ortho : persp;

  function mkControls(cam) {
    var c = new OrbitControls(cam, renderer.domElement);
    c.enableDamping = true; c.dampingFactor = 0.08;
    c.enablePan = true; c.screenSpacePanning = true;   // hold RIGHT mouse = pan up/down/left/right
    c.maxPolarAngle = Math.PI * 0.92;
    c.rotateSpeed = 0.9; c.zoomSpeed = 0.9;
    c.zoomToCursor = true;   // wheel zooms toward the cursor
    return c;
  }
  var pControls = mkControls(persp);
  var oControls = mkControls(ortho);
  var controls = projection === 'isometric' ? oControls : pControls;
  pControls.enabled = (controls === pControls);
  oControls.enabled = (controls === oControls);

  // Auto-rotate is a smooth turntable: its speed eases 0 <-> max instead of
  // snapping, so starting/stopping the spin glides.
  var AR_MAX = 1.1;
  var autoRotate = root.getAttribute('data-autorotate') !== '0';
  autoRotate = autoRotate && !prefersReduced;
  var arCur = autoRotate ? AR_MAX : 0;

  var meshes = [];
  var spinGroup = null;   // wraps the model so AUTO-ROTATE spins the MODEL (the scene/floor stays put)
  var checker = null, uvMat = null, edgeMat = null;
  var mode = 'shaded';
  var accent = 0x5A3E30, wireColor = null;
  try { var cssAccent = getComputedStyle(root).getPropertyValue('--g3d-accent').trim(); if (cssAccent) accent = new THREE.Color(cssAccent).getHex(); } catch (e) {}
  try { var wc = root.getAttribute('data-wire-color'); if (wc) wireColor = new THREE.Color(wc).getHex(); } catch (e) {}
  if (wireColor == null) wireColor = accent;

  // Gizmo axis colours come from the theme settings (data attrs), with sensible
  // Blender-ish defaults.
  var axCol = { x: '#e2536b', y: '#8bc34a', z: '#4f8cff' };
  try { var ax = root.getAttribute('data-axis-x'); if (ax) axCol.x = ax; } catch (e) {}
  try { var ay = root.getAttribute('data-axis-y'); if (ay) axCol.y = ay; } catch (e) {}
  try { var az = root.getAttribute('data-axis-z'); if (az) axCol.z = az; } catch (e) {}

  var R = 1, DIST = 3;
  // Where the ground sits. Decided ONCE, from the pose the page opens on, and
  // never moved again: on an animated model the lowest point wanders frame to
  // frame, and a floor that chases it reads as the ground heaving rather than
  // the model moving.
  var groundY = 0;
  // Light position in spherical terms. Default = upper-left-front but lowered a
  // touch and nudged right, so the cast shadow falls to the RIGHT and a little
  // back, sitting close to the model base.
  var LIGHT_AZ0 = 2.45;
  var LIGHT_EL0 = Math.PI / 4.2;
  var lightAz = LIGHT_AZ0;
  var lightEl = LIGHT_EL0;

  function bumpShadow() { renderer.shadowMap.needsUpdate = true; }

  function applyLightRig() {
    var ce = Math.cos(lightEl), se = Math.sin(lightEl);
    key.position.set(ce * Math.cos(lightAz) * R * 5, se * R * 5, ce * Math.sin(lightAz) * R * 5);
    fill.position.set(-R * 3.5, R * 1.4, R * 2.0);
    rim.position.set(-R * 1.0, R * 4.0, -R * 4.5);
    sgMat.uniforms.uOffset.value.set(0, 0);   // contact stays directly under the model (where it rests)
    bumpShadow();
  }

  function updateOrthoFrustum() {
    var w = stage.clientWidth || 1, h = stage.clientHeight || 1;
    var aspect = w / h, fit = R * 1.5;
    ortho.top = fit; ortho.bottom = -fit; ortho.left = -fit * aspect; ortho.right = fit * aspect;
    ortho.near = 0.01; ortho.far = R * 100;
    ortho.updateProjectionMatrix();
  }

  function resize() {
    var w = stage.clientWidth || 1, h = stage.clientHeight || 1;
    renderer.setSize(w, h, false);
    persp.aspect = w / h; persp.updateProjectionMatrix();
    updateOrthoFrustum();
    GIZMO_DIM = Math.max(56, Math.min(96, Math.round(w * 0.2)));   // shrink the gizmo on phones
    positionDot();
    bumpShadow();
  }
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(resize).observe(stage);
  else window.addEventListener('resize', resize);

  var TARGET_SIZE = 2.0;
  var baseScale = null;

  function frame(object) {
    var box = new THREE.Box3().setFromObject(object, true);
    var size = box.getSize(new THREE.Vector3());
    var maxDim = Math.max(size.x, size.y, size.z) || 1;
    object.scale.multiplyScalar(TARGET_SIZE / maxDim);
    object.updateMatrixWorld(true);

    box.setFromObject(object, true);
    object.position.sub(box.getCenter(new THREE.Vector3()));  // centre on ROOT/origin
    object.updateMatrixWorld(true);
    baseScale = object.scale.clone();

    box.setFromObject(object, true);
    R = box.getBoundingSphere(new THREE.Sphere()).radius || 1;
    DIST = (R / Math.sin(persp.fov * Math.PI / 360)) * 1.35;

    persp.near = R / 100; persp.far = R * 100; persp.updateProjectionMatrix();
    updateOrthoFrustum();
    pControls.target.set(0, 0, 0); oControls.target.set(0, 0, 0);
    pControls.minDistance = R * 0.4; pControls.maxDistance = DIST * 3;
    lightTarget.position.set(0, 0, 0); lightTarget.updateMatrixWorld();

    // Directional cast shadow: frame the shadow camera + place the catcher plane.
    var sc = R * 1.9;
    key.shadow.camera.left = -sc; key.shadow.camera.right = sc; key.shadow.camera.top = sc; key.shadow.camera.bottom = -sc;
    key.shadow.camera.near = R * 0.05; key.shadow.camera.far = R * 22;
    key.shadow.normalBias = 0;   // zero: normalBias offsets the lookup along the floor normal, which at a grazing light = a horizontal gap at the base (worse the taller the object). depth `bias` alone handles acne.
    key.shadow.camera.updateProjectionMatrix();
    groundY = box.min.y;   // the base of the pose we are framing - the floor from here on
    castGround.scale.set(R * 16, R * 16, 1);
    castGround.position.y = groundY - R * 0.0002;   // right at the base
    castGround.visible = shadowsOn;

    // Fallback AO blob position/size (only shown if the baked shadow fails).
    var footprint = Math.max(size.x, size.z) * (TARGET_SIZE / maxDim);
    contact.scale.set(footprint * 1.7 || R * 2.4, footprint * 1.7 || R * 2.4, 1);
    contact.position.set(0, groundY - R * 0.0009, 0);

    applyLightRig();
  }

  // ---- View presets (direction only - projection is independent) ----
  var VIEW_DIRS = {
    'home':  new THREE.Vector3(0.62, 0.42, 0.86),
    'front': new THREE.Vector3(0, 0.12, 1),
    'back':  new THREE.Vector3(0, 0.12, -1),
    'left':  new THREE.Vector3(-1, 0.12, 0),
    'right': new THREE.Vector3(1, 0.12, 0),
    'top':   new THREE.Vector3(0, 1, 0.0001),
    'bottom':new THREE.Vector3(0, -1, 0.0001)
  };
  function markActive(sel, val) {
    root.querySelectorAll('[' + sel + ']').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute(sel) === val);
    });
  }
  function applyCamera(dir) {
    controls.target.set(0, 0, 0);
    camera.position.copy(dir).multiplyScalar(DIST);
    if (projection === 'isometric') { ortho.zoom = 1; ortho.updateProjectionMatrix(); }
    camera.lookAt(0, 0, 0);
    controls.update();
  }

  // Smooth eased camera glide. We interpolate on the ORBIT SPHERE (radius +
  // azimuth + polar), NOT in a straight line - a linear lerp between two
  // opposite views would fly the camera straight THROUGH the model.
  var animating = false, animStart = 0, animDur = 0;
  var sphFrom = new THREE.Spherical(), sphTo = new THREE.Spherical(), sphCur = new THREE.Spherical();
  var animOff = new THREE.Vector3();
  function nowMs() { return (window.performance && performance.now) ? performance.now() : Date.now(); }
  function animateToDir(dir) {
    autoRotate = false; syncAutoBtn();
    controls.target.set(0, 0, 0);
    sphFrom.setFromVector3(animOff.copy(camera.position).sub(controls.target));
    sphTo.setFromVector3(animOff.copy(dir).multiplyScalar(DIST));
    var dT = sphTo.theta - sphFrom.theta;     // always swing the short way round
    while (dT > Math.PI) dT -= Math.PI * 2;
    while (dT < -Math.PI) dT += Math.PI * 2;
    sphTo.theta = sphFrom.theta + dT;
    animStart = nowMs();
    animDur = prefersReduced ? 0 : 600;
    animating = true;
  }
  function setView(name, instant) {
    var d = (VIEW_DIRS[name] || VIEW_DIRS.home).clone().normalize();
    if (instant) { animating = false; applyCamera(d); }
    else animateToDir(d);
    markActive('data-g3d-view', name);
  }
  function setProjection(p) {
    if (p === projection) return;
    animating = false;
    var dir = camera.position.clone().sub(controls.target).normalize();  // keep orientation
    var newCam = p === 'isometric' ? ortho : persp;
    var newCtrl = p === 'isometric' ? oControls : pControls;
    newCtrl.target.copy(controls.target);
    controls.enabled = false;
    camera = newCam; controls = newCtrl;
    controls.enabled = true;
    projection = p;
    if (p === 'isometric') { ortho.zoom = 1; updateOrthoFrustum(); }
    resize();
    applyCamera(dir);
    markActive('data-g3d-proj', p);
  }

  function applyMode(next) {
    mode = next;
    if (mode === 'uv' && !checker) checker = makeCheckerTexture();
    if (mode === 'wireframe' && !edgeMat) edgeMat = new THREE.LineBasicMaterial({ color: wireColor, transparent: true, opacity: 0.7 });
    meshes.forEach(function (rec) {
      var m = rec.mesh;
      if (mode === 'wireframe' && !rec.edges && m.geometry) {
        rec.edges = new THREE.LineSegments(new THREE.WireframeGeometry(m.geometry), edgeMat);
        rec.edges.raycast = function () {}; rec.edges.renderOrder = 1; m.add(rec.edges);
      }
      if (rec.edges) rec.edges.visible = (mode === 'wireframe');
      if (mode === 'shaded' || mode === 'wireframe') m.material = rec.original;
      else if (mode === 'uv') {
        if (!uvMat) uvMat = new THREE.MeshStandardMaterial({ map: checker, roughness: 0.85, metalness: 0.0 });
        m.material = uvMat;
      }
    });
    bumpShadow();
    markActive('data-g3d-mode', mode);
  }

  // ---- Blender-style axis gizmo (rendered into the canvas top-left corner) ----
  var GIZMO_DIM = 92;     // CSS px box for the gizmo
  var ready = false;
  var gizmoScene = new THREE.Scene();
  var gizmoCam = new THREE.OrthographicCamera(-1.7, 1.7, 1.7, -1.7, 0, 4);
  gizmoCam.position.set(0, 0, 2); gizmoCam.lookAt(0, 0, 0);
  var gizmoGroup = new THREE.Group();
  gizmoScene.add(gizmoGroup);
  var gizmoHandles = [];
  var gizmoRay = new THREE.Raycaster();
  (function buildGizmo() {
    var LEN = 1.0, BALL = 0.5;
    var defs = [
      { v: new THREE.Vector3(1, 0, 0), label: 'X', color: axCol.x, dir: VIEW_DIRS.right },
      { v: new THREE.Vector3(0, 1, 0), label: 'Y', color: axCol.y, dir: VIEW_DIRS.top },
      { v: new THREE.Vector3(0, 0, 1), label: 'Z', color: axCol.z, dir: VIEW_DIRS.front }
    ];
    var negDirs = { x: VIEW_DIRS.left, y: VIEW_DIRS.bottom, z: VIEW_DIRS.back };
    defs.forEach(function (d, i) {
      var lineMat = new THREE.LineBasicMaterial({ color: d.color, transparent: true, opacity: 0.85 });
      var g = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), d.v.clone().multiplyScalar(LEN)]);
      var line = new THREE.Line(g, lineMat); line.raycast = function () {}; gizmoGroup.add(line);

      var posMat = new THREE.SpriteMaterial({ map: gizmoBallTexture(d.color, d.label, false), depthTest: false, depthWrite: false, transparent: true });
      var posHot = new THREE.SpriteMaterial({ map: gizmoBallTexture(d.color, d.label, true), depthTest: false, depthWrite: false, transparent: true });
      var pos = new THREE.Sprite(posMat); pos.scale.set(BALL, BALL, 1); pos.position.copy(d.v).multiplyScalar(LEN);
      gizmoGroup.add(pos);
      gizmoHandles.push({ mesh: pos, dir: d.dir.clone().normalize(), cold: posMat, hot: posHot });

      var negMat = new THREE.SpriteMaterial({ map: gizmoRingTexture(d.color), depthTest: false, depthWrite: false, transparent: true });
      var neg = new THREE.Sprite(negMat); neg.scale.set(BALL * 0.86, BALL * 0.86, 1); neg.position.copy(d.v).multiplyScalar(-LEN);
      gizmoGroup.add(neg);
      var nd = (i === 0 ? negDirs.x : i === 1 ? negDirs.y : negDirs.z);
      gizmoHandles.push({ mesh: neg, dir: nd.clone().normalize() });
    });
  })();

  var loader = new GLTFLoader();
  loader.setCrossOrigin('anonymous');
  try {
    var draco = new DRACOLoader(); draco.setDecoderPath(ADDON_BASE + 'draco/'); loader.setDRACOLoader(draco);
    var ktx2 = new KTX2Loader(); ktx2.setTranscoderPath(ADDON_BASE + 'basis/'); ktx2.detectSupport(renderer); loader.setKTX2Loader(ktx2);
    loader.setMeshoptDecoder(MeshoptDecoder);
  } catch (e) { try { console.warn('[g3d] decoder setup', e); } catch (_) {} }

  function onProgress(e) {
    if (loadingTxt && e && e.total) {
      var p = Math.max(0, Math.min(100, Math.round((e.loaded / e.total) * 100)));
      loadingTxt.textContent = 'Loading 3D model ' + p + '%';
    }
  }

  var maxAniso = 1;
  try { maxAniso = renderer.capabilities.getMaxAnisotropy() || 1; } catch (e) {}
  var TEX_KEYS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap', 'bumpMap'];

  loader.load(url, function (gltf) {
    var modelRoot = gltf.scene || (gltf.scenes && gltf.scenes[0]);
    if (!modelRoot) { fail('Empty 3D scene.'); return; }
    modelRoot.traverse(function (o) {
      if (o.isMesh && o.material) {
        // Cast only, DON'T receive: self-shadow acne on the model was the "щельки";
        // with no self-receive we can use a tiny bias so the floor shadow stays
        // glued right under the object.
        o.castShadow = true; o.receiveShadow = false;
        var mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach(function (mm) {
          if (!mm) return;
          if ('envMapIntensity' in mm) mm.envMapIntensity = 1.15;
          mm.dithering = true;   // breaks banding on the model's own shading
          // Max anisotropy on every texture map = crisp detail at grazing angles
          // (kills the shimmer/"рябь" on textured surfaces).
          TEX_KEYS.forEach(function (k) { if (mm[k] && mm[k].isTexture) { mm[k].anisotropy = maxAniso; mm[k].needsUpdate = true; } });
        });
        meshes.push({ mesh: o, original: o.material });
      }
    });
    spinGroup = new THREE.Group();   // turntable pivot at the origin
    scene.add(spinGroup);
    // The same idea for MATERIALS. "Identical in Cinema, different in the
    // viewer" is answered by these three numbers, because the exporter writes
    // its own interpretation and glTF fills in the rest with defaults - and its
    // default for metalness is 1, so a material the exporter said nothing about
    // arrives as fully rough METAL, which has no diffuse colour at all.
    try {
      var _seen = [];
      modelRoot.traverse(function (o) {
        if (!o.isMesh || !o.material) return;
        (Array.isArray(o.material) ? o.material : [o.material]).forEach(function (mm) {
          if (!mm || _seen.indexOf(mm) > -1) return;
          _seen.push(mm);
          console.log('[g3d] material "' + (mm.name || '(unnamed)') + '"'
            + ' metalness=' + (mm.metalness == null ? 'n/a' : mm.metalness)
            + ' roughness=' + (mm.roughness == null ? 'n/a' : mm.roughness)
            + ' map=' + (mm.map ? 'yes' : 'no')
            + ' color=#' + (mm.color ? mm.color.getHexString() : '------'));
        });
      });
    } catch (e) {}

    // Says out loud what the FILE contained, so "the slider is missing" can be
    // answered without guessing: zero clips is an export that dropped them,
    // and a non-zero count with no slider is our bug.
    try {
      var _clips = (gltf.animations || []).map(function (c) { return c.name + ' (' + c.duration.toFixed(2) + 's)'; });
      console.log('[g3d] animation clips in file: ' + _clips.length + (_clips.length ? ' -> ' + _clips.join(', ') : ''));
    } catch (e) {}

    // A rig that came in the file. glTF carries the skeleton and the clips, so
    // there is nothing to author here - only to expose.
    if (gltf.animations && gltf.animations.length) {
      try {
        mixer = new THREE.AnimationMixer(modelRoot);
        // ALL clips at the same instant, not just the first. An exporter often
        // writes one clip per moving part, and playing only one would leave the
        // rest of the machine standing still.
        gltf.animations.forEach(function (clip) {
          var a = mixer.clipAction(clip);
          a.play();
          // NOT paused. `paused` zeroes the action's effective time scale, and
          // mixer.setTime() works by REWINDING to zero and advancing by a
          // delta - so a paused action multiplies that delta by zero and never
          // moves. In three, pause and scrub are mutually exclusive.
          // Nothing runs away as a result: the render loop never calls
          // mixer.update(), so the only thing that advances time is the slider.
          a.paused = false;
          a.enabled = true;
          a.setEffectiveTimeScale(1);
          a.setEffectiveWeight(1);
          animActions.push(a);
          clipNames.push(clip.name || ('Clip ' + animActions.length));
        });
        // Alternatives open on the first clip; parts open on all of them.
        clipsConflict = clipsOverlap(gltf.animations);
        clipSel = clipsConflict ? 0 : -1;
        buildClipMenu();
        applyClipSelection();
        // A skinned mesh is culled by its BIND-pose bounds, so a limb that
        // swings outside them makes the whole model blink out mid-scrub.
        modelRoot.traverse(function (o) { if (o.isSkinnedMesh) o.frustumCulled = false; });
        if (animPad && clipDur > 0) animPad.hidden = false;
        root.classList.add('has-anim');
      } catch (err) {
        mixer = null; animActions = []; clipNames = []; clipDur = 0;
        try { console.warn('[g3d] animation setup failed', err); } catch (_) {}
      }
    }

    spinGroup.add(modelRoot);
    // OPEN ON THE LAST FRAME.
    // An animation is authored to arrive somewhere: the assembled machine, the
    // finished fold, the character standing. That end pose is the product, so
    // it is what the page opens on, what the framing is measured from, and what
    // the floor is laid under. Doing it here, BEFORE frame(), is the whole
    // point - frame() is what measures, centres and places the ground.
    // updateMatrixWorld first: Box3 walks the vertices through the BONES, and
    // stale bone matrices would measure the pose we just left.
    if (mixer && clipDur > 0) {
      if (animRange) animRange.value = '1000';
      scrubTo(1);
      modelRoot.updateMatrixWorld(true);
    }
    frame(modelRoot);
    // Tight contact shadow under the resting footprint (fills the gap so the
    // model doesn't read as floating) + the crisp directional side shadow.
    // Shadows off: nothing is baked, nothing is shown, and the fallback blob
    // stays away too - it is a shadow as well, just a softer one.
    if (shadowsOn) {
      try { bakeShadow(); shadowBaked = true; } catch (err) { contact.visible = true; try { console.warn('[g3d] shadow bake failed', err); } catch (_) {} }
    }
    setView('home', true);   // snap to the 3/4 start (no fly-out from centre)
    applyMode('shaded');
    ready = true;
    root.classList.add('is-ready');

    if (!prefersReduced && baseScale) {
      var introStart = null, introFrom = 0.94, introTo = 1;
      modelRoot.scale.copy(baseScale).multiplyScalar(introFrom);
      var intro = function (ts) {
        if (introStart == null) introStart = ts || 0;
        var t = Math.min(1, ((ts || 0) - introStart) / 650);
        var e = 1 - Math.pow(1 - t, 3);
        modelRoot.scale.copy(baseScale).multiplyScalar(introFrom + (introTo - introFrom) * e);
        bumpShadow();
        if (t < 1) requestAnimationFrame(intro);
        else { modelRoot.scale.copy(baseScale); bumpShadow(); }
      };
      requestAnimationFrame(intro);
    }
  }, onProgress, function (err) {
    fail('Could not load the 3D model.');
    try { console.error('[g3d] load error', err); } catch (e) {}
  });

  var running = true;
  function tick() {
    if (!running) return;

    if (animating) {
      var t = animDur ? Math.min(1, (nowMs() - animStart) / animDur) : 1;
      var e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;   // easeInOutCubic
      sphCur.radius = sphFrom.radius + (sphTo.radius - sphFrom.radius) * e;
      sphCur.phi = sphFrom.phi + (sphTo.phi - sphFrom.phi) * e;
      sphCur.theta = sphFrom.theta + (sphTo.theta - sphFrom.theta) * e;
      sphCur.makeSafe();
      camera.position.copy(controls.target).add(animOff.setFromSpherical(sphCur));
      camera.lookAt(controls.target);
      if (projection === 'isometric') ortho.updateProjectionMatrix();
      if (t >= 1) animating = false;
    }

    // Auto-rotate orbits the CAMERA around the model (the original feel), easing
    // its speed on/off. Works in perspective AND isometric.
    var arTarget = (autoRotate && !animating) ? AR_MAX : 0;
    arCur += (arTarget - arCur) * 0.08;
    if (arCur < 0.004) arCur = 0;
    var spinning = arCur > 0.004;
    pControls.autoRotate = oControls.autoRotate = spinning;
    pControls.autoRotateSpeed = oControls.autoRotateSpeed = arCur;

    controls.update();

    renderer.clear();
    renderer.render(bgScene, bgCam);   // dithered gradient backdrop (fills the frame)
    renderer.render(scene, camera);

    if (ready) {
      gizmoGroup.quaternion.copy(camera.quaternion).invert();
      var W = stage.clientWidth || 1, H = stage.clientHeight || 1;
      renderer.clearDepth();
      renderer.setViewport(8, H - GIZMO_DIM - 8, GIZMO_DIM, GIZMO_DIM);   // top-left
      renderer.render(gizmoScene, gizmoCam);
      renderer.setViewport(0, 0, W, H);
    }

    requestAnimationFrame(tick);
  }
  if (typeof IntersectionObserver !== 'undefined') {
    new IntersectionObserver(function (entries) {
      var visible = entries[0] && entries[0].isIntersecting;
      if (visible && !running) { running = true; tick(); }
      else if (!visible) running = false;
    }, { threshold: 0.01 }).observe(root);
  }

  function onFirstInteract() { root.classList.add('is-interacted'); autoRotate = false; syncAutoBtn(); }
  renderer.domElement.addEventListener('pointerdown', onFirstInteract, { once: true });

  // Gizmo click: pick on pointer-UP only when it was a click (not a drag) inside
  // the top-left gizmo box; route to a smooth spherical glide.
  var downX = 0, downY = 0;
  renderer.domElement.addEventListener('pointerdown', function (e) { downX = e.clientX; downY = e.clientY; });

  // Which gizmo handle is under the pointer, or null. Shared by the hover
  // highlight and the click below, so the two can never disagree about what
  // you were pointing at.
  function gizmoPick(e) {
    if (!ready) return null;
    var rect = renderer.domElement.getBoundingClientRect();
    var lx = e.clientX - (rect.left + 8), ly = e.clientY - (rect.top + 8);
    if (lx < 0 || ly < 0 || lx > GIZMO_DIM || ly > GIZMO_DIM) return null;
    var ndc = { x: (lx / GIZMO_DIM) * 2 - 1, y: -(ly / GIZMO_DIM) * 2 + 1 };
    gizmoGroup.quaternion.copy(camera.quaternion).invert();
    gizmoGroup.updateMatrixWorld(true);
    gizmoRay.setFromCamera(ndc, gizmoCam);
    var hit = gizmoRay.intersectObjects(gizmoHandles.map(function (h) { return h.mesh; }), false);
    if (!hit.length) return null;
    for (var i = 0; i < gizmoHandles.length; i++) if (gizmoHandles[i].mesh === hit[0].object) return gizmoHandles[i];
    return null;
  }

  var gizmoHot = null;
  renderer.domElement.addEventListener('pointermove', function (e) {
    // Only a real pointer hovers. A finger mid-drag would otherwise light a
    // handle it is merely passing over on its way to rotating the model.
    if (e.pointerType === 'touch' || (e.buttons & 1)) return;
    var h = gizmoPick(e);
    if (h === gizmoHot) return;
    if (gizmoHot && gizmoHot.cold) gizmoHot.mesh.material = gizmoHot.cold;
    gizmoHot = h;
    if (gizmoHot && gizmoHot.hot) gizmoHot.mesh.material = gizmoHot.hot;
    // The cursor says "this one is clickable" before the colour does.
    renderer.domElement.style.cursor = gizmoHot ? 'pointer' : '';
  });
  renderer.domElement.addEventListener('pointerleave', function () {
    if (gizmoHot && gizmoHot.cold) gizmoHot.mesh.material = gizmoHot.cold;
    gizmoHot = null;
    renderer.domElement.style.cursor = '';
  });
  renderer.domElement.addEventListener('pointerup', function (e) {
    if (!ready) return;
    if (Math.hypot(e.clientX - downX, e.clientY - downY) > 5) return;   // was a drag
    var picked = gizmoPick(e);
    if (picked) animateToDir(picked.dir.clone());
  });

  // ---- Modes / projection / home ----
  root.querySelectorAll('[data-g3d-mode]').forEach(function (btn) { btn.addEventListener('click', function () { applyMode(btn.getAttribute('data-g3d-mode')); }); });
  root.querySelectorAll('[data-g3d-view]').forEach(function (btn) { btn.addEventListener('click', function () { setView(btn.getAttribute('data-g3d-view')); }); });
  root.querySelectorAll('[data-g3d-proj]').forEach(function (btn) { btn.addEventListener('click', function () { setProjection(btn.getAttribute('data-g3d-proj')); }); });
  markActive('data-g3d-proj', projection);

  var autoBtn = root.querySelector('[data-g3d-autorotate]');
  function syncAutoBtn() { if (!autoBtn) return; autoBtn.classList.toggle('is-active', autoRotate); autoBtn.setAttribute('aria-pressed', autoRotate ? 'true' : 'false'); }
  if (autoBtn) { autoBtn.addEventListener('click', function () { autoRotate = !autoRotate; syncAutoBtn(); }); syncAutoBtn(); }

  // ---- Shadow on/off, live ----
  // `shadowsOn` starts from the section setting; this only ever turns it off
  // and on again afterwards, so a merchant who ships it off never renders a
  // shadow pass at all, and a visitor who wants one still gets it.
  // ---- Animation scrubbing ----
  // Deliberately NOT a play button. On a product page nobody wants to watch a
  // loop; they want to stop on the pose that answers their question. A scrubber
  // also means the model moves ONLY while a hand is on it: no autoplay, nothing
  // fighting the turntable, and no frames burned on a phone that is just
  // sitting there.
  // clipDur, NOT animDur: `animDur` is the CAMERA GLIDE's duration a few
  // hundred lines up. Sharing the name meant every view button overwrote the
  // clip length with 600, and the slider then scrubbed to six hundred seconds.
  var mixer = null, animActions = [], clipDur = 0;
  var clipNames = [];
  var clipsConflict = false;
  var clipSel = -1;             // -1 = every clip at once; otherwise the index of the one
  var animPad = root.querySelector('[data-g3d-anim]');
  var animRange = root.querySelector('[data-g3d-anim-range]');
  var animPick = root.querySelector('[data-g3d-anim-pick]');
  var animToggle = root.querySelector('[data-g3d-anim-toggle]');
  var animMenu = root.querySelector('[data-g3d-anim-menu]');
  var reBakeTimer = null;

  var shadowBtn = root.querySelector('[data-g3d-shadow]');
  var shadowBaked = false;
  function applyShadows() {
    renderer.shadowMap.enabled = shadowsOn;
    key.castShadow = shadowsOn;
    if (shadowsOn && !shadowBaked && ready) {
      // First time on: the bake was skipped at load, so do it now.
      try { bakeShadow(); shadowBaked = true; } catch (err) { try { console.warn('[g3d] shadow bake failed', err); } catch (_) {} }
    }
    castGround.visible = shadowsOn;
    shadowGround.visible = shadowsOn && shadowBaked;
    contact.visible = false;
    if (shadowBtn) {
      shadowBtn.classList.toggle('is-active', shadowsOn);
      shadowBtn.setAttribute('aria-pressed', shadowsOn ? 'true' : 'false');
    }
    bumpShadow();
  }
  if (shadowBtn) shadowBtn.addEventListener('click', function () { shadowsOn = !shadowsOn; applyShadows(); });

  function scrubTo(frac) {
    if (!mixer || clipDur <= 0) return;
    var f = Math.max(0, Math.min(1, frac));
    // Held a hair short of the end: a looping clip wraps back to frame zero at
    // exactly its duration, so the far right of the slider would snap the model
    // to the pose at the far left.
    mixer.setTime(Math.min(f * clipDur, clipDur - 0.0001));
    // The cast shadow is baked on the assumption that nothing moves. It has to
    // be re-rendered as the pose changes, or the model slides out from under
    // its own shadow.
    bumpShadow();
    // The soft contact patch is a two-pass render into a texture - too costly
    // per frame, so it waits for the hand to stop.
    if (shadowsOn && shadowBaked) {
      if (reBakeTimer) clearTimeout(reBakeTimer);
      reBakeTimer = setTimeout(function () {
        reBakeTimer = null;
        try { bakeShadow(); } catch (e) {}
      }, 220);
    }
  }
  // Which clip (or all of them) is live. Weight, not stop: an action switched
  // off with `enabled = false` lets three blend the properties it drives back
  // toward their ORIGINAL values, so the nodes a dropped clip was moving return
  // to rest instead of freezing in its last pose.
  function applyClipSelection() {
    var maxD = 0;
    animActions.forEach(function (a, i) {
      var on = (clipSel < 0) || (i === clipSel);
      a.enabled = on;
      a.setEffectiveWeight(on ? 1 : 0);
      if (on) { var d = a.getClip().duration; if (d > maxD) maxD = d; }
    });
    // The slider spans the SELECTION, not the file. A 0.7s walk mapped onto a
    // 3.4s scale would live in the last fifth of the track.
    clipDur = maxD;
    if (animMenu) {
      var rows = animMenu.querySelectorAll('[data-g3d-clip]');
      for (var r = 0; r < rows.length; r++) {
        var live = parseInt(rows[r].getAttribute('data-g3d-clip'), 10) === clipSel;
        rows[r].classList.toggle('is-active', live);
        rows[r].setAttribute('aria-checked', live ? 'true' : 'false');
      }
    }
    if (animToggle) {
      var label = clipSel < 0 ? 'All together' : (clipNames[clipSel] || ('Clip ' + (clipSel + 1)));
      animToggle.title = 'Animation: ' + label;
      animToggle.setAttribute('aria-label', 'Animation: ' + label + '. Choose another');
    }
  }

  function selectClip(i) {
    clipSel = i;
    applyClipSelection();
    // A newly chosen clip opens on ITS end pose, the same way the model did.
    // The floor stays where it was laid: it belongs to the product, not to
    // whichever clip is being inspected.
    if (animRange) animRange.value = '1000';
    scrubTo(1);
  }

  function buildClipMenu() {
    if (!animMenu || !animPick || clipNames.length < 2) return;   // one clip needs no menu
    var opts = [];
    if (!clipsConflict) opts.push([-1, 'All together']);
    clipNames.forEach(function (n, i) { opts.push([i, n]); });
    animMenu.innerHTML = '';
    opts.forEach(function (o) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'g3d-anim-opt';
      b.setAttribute('role', 'menuitemradio');
      b.setAttribute('data-g3d-clip', String(o[0]));
      b.textContent = o[1];
      b.addEventListener('click', function () { selectClip(o[0]); openClipMenu(false); });
      animMenu.appendChild(b);
    });
    animPick.hidden = false;
  }

  function openClipMenu(open) {
    if (!animMenu || !animToggle) return;
    animMenu.hidden = !open;
    animToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  if (animToggle && animMenu) {
    animToggle.addEventListener('click', function (e) {
      e.stopPropagation();
      openClipMenu(animMenu.hidden);
    });
    // Anywhere else dismisses it, including the model - a menu left hanging
    // over the object being inspected is the thing in the way.
    document.addEventListener('click', function (e) {
      if (animMenu.hidden) return;
      if (animPick && animPick.contains(e.target)) return;
      openClipMenu(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !animMenu.hidden) { openClipMenu(false); animToggle.focus(); }
    });
  }

  if (animRange) {
    animRange.addEventListener('input', function () {
      // The wireframe overlay is built from the BIND-pose geometry and skinning
      // happens on the GPU, so it cannot follow the bones. Rather than show a
      // cage that lies about where the model is, scrubbing returns to Shaded.
      if (mode === 'wireframe') applyMode('shaded');
      scrubTo(parseFloat(animRange.value) / 1000);
    });
  }

  var resetBtn = root.querySelector('[data-g3d-reset]');
  if (resetBtn) resetBtn.addEventListener('click', function () { lightAz = LIGHT_AZ0; lightEl = LIGHT_EL0; applyLightRig(); positionDot(); if (spinGroup) { spinGroup.rotation.y = 0; bumpShadow(); } if (projection !== START_PROJ) setProjection(START_PROJ); setView('home'); if (animRange && clipDur > 0) { animRange.value = '1000'; scrubTo(1); } });

  var fsBtn = root.querySelector('[data-g3d-fs]');
  if (fsBtn) fsBtn.addEventListener('click', function () {
    if (document.fullscreenElement === root) { if (document.exitFullscreen) document.exitFullscreen(); }
    else if (root.requestFullscreen) root.requestFullscreen().then(resize).catch(function () {});
  });
  document.addEventListener('fullscreenchange', function () { setTimeout(resize, 60); });

  // ---- Light sphere (top-right): drag a dot over a disc. Angle from centre =
  //      azimuth; distance from centre = elevation (centre = overhead, edge =
  //      horizon). ----
  var lightPad = root.querySelector('[data-g3d-light]');
  var lightDot = lightPad ? lightPad.querySelector('.g3d-light-dot') : null;
  function positionDot() {
    if (!lightDot || !lightPad) return;
    var rad = (lightPad.clientWidth || 44) * 0.34;
    var rr = rad * (1 - lightEl / (Math.PI / 2));
    lightDot.style.transform = 'translate(' + (Math.cos(lightAz) * rr).toFixed(1) + 'px,' + (Math.sin(lightAz) * rr).toFixed(1) + 'px)';
  }
  if (lightPad) {
    var dragging = false;
    function setFrom(e) {
      var r = lightPad.getBoundingClientRect();
      var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      var pt = e.touches ? e.touches[0] : e;
      var dx = pt.clientX - cx, dy = pt.clientY - cy;
      var rad = (lightPad.clientWidth || 44) * 0.34;
      var dist = Math.min(1, Math.hypot(dx, dy) / rad);
      lightAz = Math.atan2(dy, dx);
      lightEl = (1 - dist) * (Math.PI / 2);
      if (lightEl < 0.18) lightEl = 0.18;   // keep light off the floor
      applyLightRig(); positionDot();
    }
    function move(e) { if (!dragging) return; e.preventDefault(); setFrom(e); }
    function up() { dragging = false; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); }
    lightPad.addEventListener('pointerdown', function (e) { e.preventDefault(); dragging = true; setFrom(e); window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); });
  }

  resize();
  tick();
}

function boot() {
  var nodes = document.querySelectorAll('[data-g3d]');
  if (!nodes.length) return;
  if (typeof IntersectionObserver === 'undefined') { nodes.forEach(initViewer); return; }
  var io = new IntersectionObserver(function (entries, obs) {
    entries.forEach(function (en) { if (en.isIntersecting) { obs.unobserve(en.target); initViewer(en.target); } });
  }, { rootMargin: '300px' });
  nodes.forEach(function (n) { io.observe(n); });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
